import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import {
	ChemistryElement,
	ChemistryRangeByGrade,
	ChemistryRangeLimit,
	ChemistryRangeVersion,
	ChemistryRangesPayload,
	ChemistryRangeRowDraft,
} from "../models/ChemistryRange";

export const getChemistryRangeVersions = async (signal?: AbortSignal) => {
	const apiService = new MESApiService();

	const resp = await apiService.callV2(
		"[LAB].[GetChemistryRangeVersions]",
		[],
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as ChemistryRangeVersion[];
	} else {
		throw new Error(resp.message || "Error fetching chemistry range versions");
	}
};

export const getChemistryRangesByGrade = async (
	params: { versionId?: number | null },
	signal?: AbortSignal,
): Promise<ChemistryRangesPayload> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "ChemistryRangeVersionId", value: params.versionId ?? null },
	];

	const resp = await apiService.callV2(
		"[LAB].[GetChemistryRangesByGrade]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return {
			rows: get(resp, "data.tables[0].rows", []) as ChemistryRangeByGrade[],
			limits: get(resp, "data.tables[1].rows", []) as ChemistryRangeLimit[],
			elements: get(resp, "data.tables[2].rows", []) as ChemistryElement[],
		};
	} else {
		throw new Error(resp.message || "Error fetching chemistry ranges");
	}
};

export const upsertChemistryRanges = async ({
	versionId,
	comments,
	rows,
}: {
	versionId: number | null;
	comments: string | null;
	rows: ChemistryRangeRowDraft[];
}) => {
	const apiService = new MESApiService();

	const payload = rows.map((row) => ({
		SWV: row.SWV,
		GradeDescription: row.GradeDescription,
		Customer: row.Customer,
		COP: row.COP,
		Additions: row.Additions,
		Limits: [
			...Object.entries(row.Limits)
				.filter(
					([, limit]) => limit.MinValue !== null || limit.MaxValue !== null,
				)
				.map(([element, limit]) => ({
					Element: element,
					SectionId: null as number | null,
					MinValue: limit.MinValue,
					MaxValue: limit.MaxValue,
				})),
			...row.SectionLimits.map((limit) => ({
				Element: limit.Element,
				SectionId: limit.SectionId,
				MinValue: limit.MinValue,
				MaxValue: limit.MaxValue,
			})),
		],
	}));

	const parameters: spParameter[] = [
		{ name: "ChemistryRangeVersionId", value: versionId },
		{ name: "Comments", value: comments },
		{ name: "Rows", value: JSON.stringify(payload) },
	];

	const resp = await apiService.callV2(
		"[LAB].[UpsertChemistryRanges]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error saving chemistry ranges");
	}
};

export const confirmChemistryRangeVersion = async (params: {
	versionId: number;
	rev: number;
	effectiveDate: Date;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "ChemistryRangeVersionId", value: params.versionId },
		{ name: "Rev", value: params.rev },
		{ name: "EffectiveDate", value: params.effectiveDate.toISOString() },
	];

	const resp = await apiService.callV2(
		"[LAB].[ConfirmChemistryRangeVersion]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error confirming chemistry range version");
	}
};

export const unscheduleChemistryRangeVersion = async (params: {
	versionId: number;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "ChemistryRangeVersionId", value: params.versionId },
	];

	const resp = await apiService.callV2(
		"[LAB].[UnscheduleChemistryRangeVersion]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(
			resp.message || "Error sending the chemistry range version back to Draft",
		);
	}
};

export const deleteChemistryRangeVersion = async (params: {
	versionId: number;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "ChemistryRangeVersionId", value: params.versionId },
	];

	const resp = await apiService.callV2(
		"[LAB].[DeleteChemistryRangeVersion]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error discarding chemistry range version");
	}
};
